import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api, errMsg, getAccessToken } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useLookup } from "../context/LookupContext.js";
import { DropdownSelect } from "./DropdownButton.js";
import { AsyncButton } from "./ui/async-button.js";
import { FileInput } from "./ui/file-input.js";
import { AssetImportReview } from "./AssetImportReview.js";

interface Office { id: string; name: string; city: string }

interface ImportBatch {
  id: string; fileName: string; fileFormat: string; status: string;
  totalRows: number; validRows: number; errorRows: number; importedRows: number;
  skippedRows: number; matchedRows: number; unmatchedRows: number;
  uploadedAt: string; committedAt: string | null;
  office: { id: string; name: string; city: string } | null;
  uploadedBy: { id: string; firstName: string; lastName: string };
}
interface StagedResult extends ImportBatch {
  ignoredColumns: string[];
  preview: Record<string, string>[];
}
interface CommitResult { processed: number; created: number; failed: number; remaining: number; batchStatus: string }

const inputStyle: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "14px",
  background: "var(--color-surface-0)", width: "100%", boxSizing: "border-box",
};
const btnStyle = (color = "var(--color-brand-600)", text = "#fff"): React.CSSProperties => ({
  padding: "6px 14px", background: color, color: text,
  border: "none", borderRadius: "var(--radius-md)", cursor: "pointer",
  fontWeight: 600, fontSize: "13px",
});
const chip = (bg: string, fg: string): React.CSSProperties => ({
  display: "inline-block", fontSize: 11, padding: "3px 10px", borderRadius: 999, fontWeight: 700, background: bg, color: fg,
});

export function AssetImportPanel({ offices, isRegionalScoped, myOfficeId }: { offices: Office[]; isRegionalScoped: boolean; myOfficeId: string }) {
  const categories = useLookup("ASSET_CATEGORY");
  const [category, setCategory] = useState("LAPTOP");
  const [officeId, setOfficeId] = useState(isRegionalScoped ? myOfficeId : "");
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Before the lookup loads there are no categories, so the panel falls back
  // to the fixed enum rather than offering an empty list.
  const categoryOptions = useMemo(
    () => categories.length > 0
      ? categories.map(c => ({ value: c.value, label: c.displayName }))
      : ["LAPTOP", "DESKTOP", "MONITOR", "MOBILE", "PERIPHERAL", "NETWORKING", "FURNITURE", "SOFTWARE_LICENSE", "TELECOM_SERVICE", "OTHER"]
          .map(c => ({ value: c, label: c })),
    [categories]
  );
  const officeOptions = useMemo(
    () => offices.map(o => ({ value: o.id, label: `${o.name} — ${o.city}` })),
    [offices]
  );
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [staged, setStaged] = useState<StagedResult | null>(null);
  const [err, setErr] = useState("");

  const [committing, setCommitting] = useState(false);
  const [progress, setProgress] = useState({ created: 0, failed: 0, remaining: 0 });

  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [reviewingBatchId, setReviewingBatchId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadBatches = useCallback(async () => {
    try {
      const { data } = await api.get<{ data: ImportBatch[] }>("/asset-imports");
      setBatches(data.data);
    } catch (e) { logError("AssetImportPanel.loadBatches", e); }
  }, []);

  useEffect(() => { void loadBatches(); }, [loadBatches]);

  function downloadTemplate() {
    const token = getAccessToken() ?? "";
    fetch(`/api/asset-imports/template?category=${category}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `asset-import-template-${category.toLowerCase()}.csv`; a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e) => { logError("AssetImportPanel.downloadTemplate", e); setErr("Template download failed"); });
  }

  async function handleUpload() {
    if (!file) throw new Error("validation");
    if (!isRegionalScoped && !officeId) { setErr("Office is required"); throw new Error("validation"); }
    setUploading(true); setErr(""); setStaged(null);
    try {
      const form = new FormData();
      form.append("file", file);
      if (officeId) form.append("officeId", officeId);
      form.append("category", category);
      const { data } = await api.post<StagedResult>("/asset-imports", form);
      setStaged(data);
      setProgress({ created: 0, failed: 0, remaining: data.validRows });
      void loadBatches();
    } catch (e) {
      setErr(errMsg(e) ?? "Upload failed");
      throw e;
    } finally {
      setUploading(false);
    }
  }

  async function runCommit(batchId: string) {
    setCommitting(true); setErr("");
    try {
      let remaining = 1;
      while (remaining > 0) {
        const { data } = await api.post<CommitResult>(`/asset-imports/${batchId}/commit`, {});
        setProgress((p) => ({ created: p.created + data.created, failed: p.failed + data.failed, remaining: data.remaining }));
        remaining = data.remaining;
      }
      void loadBatches();
    } catch (e) {
      setErr(errMsg(e) ?? "Commit failed");
      throw e;
    } finally {
      setCommitting(false);
    }
  }

  if (reviewingBatchId) {
    return (
      <AssetImportReview
        batchId={reviewingBatchId}
        onBack={() => { setReviewingBatchId(null); void loadBatches(); }}
      />
    );
  }

  const commitTotal = progress.created + progress.failed + progress.remaining;
  const commitPct = commitTotal > 0 ? ((progress.created + progress.failed) / commitTotal) * 100 : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {err && <p style={{ color: "var(--color-error)", fontSize: 13, margin: 0 }}>{err}</p>}

      <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20 }}>
        <h3 style={{ fontWeight: 700, fontSize: 15, margin: "0 0 14px" }}>Upload asset file</h3>
        <p style={{ fontSize: 13, color: "var(--color-text-500)", margin: "0 0 14px" }}>
          Upload a CSV or Excel (.xlsx) file. Unknown asset owners are staged for review — nothing is created
          until you commit.
        </p>

        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 14, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Default category</label>
            <DropdownSelect
              value={category}
              onChange={setCategory}
              options={categoryOptions}
              placeholder="Select category…"
              ariaLabel="Default category"
              clearable={false}
              className="w-full justify-between"
              open={openDropdown === "category"}
              onToggle={() => setOpenDropdown(d => (d === "category" ? null : "category"))}
              onClose={() => setOpenDropdown(d => (d === "category" ? null : d))}
            />
          </div>
          {!isRegionalScoped && (
            <div style={{ flex: "1 1 200px" }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Office *</label>
              <DropdownSelect
                value={officeId}
                onChange={setOfficeId}
                options={officeOptions}
                placeholder="Select office…"
                ariaLabel="Office"
                clearable={false}
                className="w-full justify-between"
                open={openDropdown === "office"}
                onToggle={() => setOpenDropdown(d => (d === "office" ? null : "office"))}
                onClose={() => setOpenDropdown(d => (d === "office" ? null : d))}
              />
            </div>
          )}
          <button onClick={downloadTemplate} style={btnStyle("var(--color-surface-100)", "var(--color-text-700)")}>Download Template</button>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <FileInput
            ref={fileInputRef} accept=".csv,.txt,.xlsx,.xls" placeholder="Choose import file"
            value={file} onChange={f => { setFile(f); setStaged(null); }}
          />
          <AsyncButton
            onClick={() => handleUpload()}
            disabled={uploading || !file || (!isRegionalScoped && !officeId)}
          >
            Upload & Stage
          </AsyncButton>
        </div>
        <p style={{ fontSize: 11, color: "var(--color-text-400)", margin: "8px 0 0" }}>Max 5,000 rows, 10MB per file.</p>
      </div>

      {staged && (
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20 }}>
          <h3 style={{ fontWeight: 700, fontSize: 15, margin: "0 0 10px" }}>Staged: {staged.fileName}</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            <span style={chip("var(--color-surface-100)", "var(--color-text-700)")}>{staged.totalRows} total</span>
            <span style={chip("var(--color-success-bg)", "var(--color-success-fg)")}>{staged.validRows} valid</span>
            <span style={chip("var(--color-error-bg)", "var(--color-error-fg)")}>{staged.errorRows} errors</span>
            <span style={chip("var(--color-info-bg)", "var(--color-info-fg)")}>{staged.matchedRows} matched</span>
            <span style={chip("var(--color-warning-bg)", "var(--color-warning-fg)")}>{staged.unmatchedRows} unmatched employee</span>
          </div>
          {staged.ignoredColumns.length > 0 && (
            <p style={{ fontSize: 12, color: "var(--color-text-400)", margin: "0 0 14px" }}>
              Ignored columns (not recognized): {staged.ignoredColumns.join(", ")}
            </p>
          )}

          {staged.errorRows > 0 && (
            <button onClick={() => setReviewingBatchId(staged.id)} style={{ ...btnStyle("var(--color-warning-bg)", "var(--color-warning-fg)"), marginBottom: 14 }}>
              Review {staged.errorRows} exception(s)
            </button>
          )}

          {staged.validRows > 0 && (
            <div style={{ marginBottom: 14 }}>
              <AsyncButton onClick={() => runCommit(staged.id)} disabled={committing} progress={commitPct}>
                {`Commit ${staged.validRows} valid row(s)`}
              </AsyncButton>
              {(progress.created > 0 || progress.failed > 0) && !committing && (
                <span style={{ marginLeft: 10, fontSize: 13, color: "var(--color-text-500)" }}>
                  {progress.created} created{progress.failed > 0 ? `, ${progress.failed} failed` : ""}
                </span>
              )}
            </div>
          )}

          {staged.preview.length > 0 && (
            <div style={{ overflowX: "auto", maxHeight: 260, overflowY: "auto", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead style={{ position: "sticky", top: 0, background: "var(--color-surface-50)" }}>
                  <tr>{Object.keys(staged.preview[0] ?? {}).map((h) => (
                    <th key={h} style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "var(--color-text-500)", whiteSpace: "nowrap" }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {staged.preview.map((row, i) => (
                    <tr key={i} style={{ borderTop: "1px solid var(--color-surface-100)" }}>
                      {Object.values(row).map((v, j) => <td key={j} style={{ padding: "5px 10px", whiteSpace: "nowrap" }}>{String(v)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", overflow: "hidden" }}>
        <h3 style={{ fontWeight: 700, fontSize: 15, margin: 0, padding: "16px 20px 0" }}>Import history</h3>
        {batches.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--color-text-400)", padding: 20 }}>No imports yet.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 10 }}>
            <thead><tr style={{ background: "var(--color-surface-50)", borderTop: "1px solid var(--color-surface-200)", borderBottom: "1px solid var(--color-surface-200)" }}>
              {["File", "Status", "Rows", "Imported", "Exceptions", "Uploaded By", ""].map((h) => (
                <th key={h} style={{ textAlign: "left", padding: "8px 14px", color: "var(--color-text-500)", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                  <td style={{ padding: "8px 14px" }}>{b.fileName}</td>
                  <td style={{ padding: "8px 14px" }}>
                    <span style={chip(
                      b.status === "COMPLETED" ? "var(--color-success-bg)" : b.status === "CANCELLED" ? "var(--color-error-bg)" : "var(--color-info-bg)",
                      b.status === "COMPLETED" ? "var(--color-success-fg)" : b.status === "CANCELLED" ? "var(--color-error-fg)" : "var(--color-info-fg)",
                    )}>{b.status}</span>
                  </td>
                  <td style={{ padding: "8px 14px" }}>{b.totalRows}</td>
                  <td style={{ padding: "8px 14px" }}>{b.importedRows}</td>
                  <td style={{ padding: "8px 14px" }}>{b.errorRows}</td>
                  <td style={{ padding: "8px 14px" }}>{b.uploadedBy.firstName} {b.uploadedBy.lastName}</td>
                  <td style={{ padding: "8px 14px" }}>
                    <button onClick={() => setReviewingBatchId(b.id)} style={btnStyle("var(--color-surface-100)", "var(--color-text-700)")}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
